import { fetchAuthSession } from '@aws-amplify/auth';
import { refreshAuthToken } from './authUtils';

/**
 * Wipe every key Amplify might use for Cognito tokens, plus our own
 * `cognito_token`. signOut() is supposed to do this, but when the refresh
 * token is dead the underlying RevokeToken call fails and Amplify can
 * short-circuit before clearing local state — leaving valid-looking
 * idToken/accessToken behind that the next page load picks back up.
 */
export function clearCognitoStorage(): void {
  localStorage.removeItem('cognito_token');
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('CognitoIdentityServiceProvider.')) {
      localStorage.removeItem(key);
    }
  }
}

/**
 * Mutex shared between RTK Query's baseQueryWithTokenRefresh and authenticatedFetch.
 * One refresh attempt at a time; everyone else waits and retries against the
 * refreshed session.
 *
 * Acquire-waiters take ownership of the lock when their turn comes (release()
 * passes the lock to the next acquirer). Observe-waiters (`waitForUnlock`)
 * just want to know when the in-flight critical section finishes — they do not
 * take the lock. They live in a separate queue so release() can resolve them
 * all without confusing them with acquire-waiters; otherwise release() would
 * fire an observer's callback (which doesn't take the lock or release it),
 * leaving the mutex stuck `locked = true` with no owner.
 */
export class Mutex {
  private locked = false;
  private acquireWaiters: Array<() => void> = [];
  private observeWaiters: Array<() => void> = [];

  async acquire(): Promise<() => void> {
    return new Promise((resolve) => {
      if (!this.locked) {
        this.locked = true;
        resolve(() => this.release());
      } else {
        this.acquireWaiters.push(() => {
          this.locked = true;
          resolve(() => this.release());
        });
      }
    });
  }

  private release(): void {
    // Notify all observers — they do not take the lock.
    while (this.observeWaiters.length > 0) {
      const observer = this.observeWaiters.shift();
      observer?.();
    }
    // Hand the lock to the next acquirer, or unlock if no one's waiting.
    if (this.acquireWaiters.length > 0) {
      const next = this.acquireWaiters.shift();
      next?.();
    } else {
      this.locked = false;
    }
  }

  isLocked(): boolean {
    return this.locked;
  }

  async waitForUnlock(): Promise<void> {
    if (!this.locked) {
      return;
    }
    return new Promise((resolve) => {
      this.observeWaiters.push(resolve);
    });
  }
}

export const authMutex = new Mutex();

/**
 * Pull the current id token from Amplify. Amplify auto-refreshes when the cached
 * token is expired. Falls back to localStorage if Amplify itself errors (test mode
 * seeds localStorage directly without an Amplify session).
 */
export async function getAuthIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString() ?? null;
    if (token) {
      localStorage.setItem('cognito_token', token);
      return token;
    }
  } catch (err) {
    console.warn('getAuthIdToken: fetchAuthSession failed', err);
  }
  return localStorage.getItem('cognito_token');
}

/**
 * Run `retry` after a refresh attempt. Returns the retried result, or null if the
 * refresh failed and the user has been signed out. Both this and RTK Query's
 * baseQueryWithTokenRefresh use the same `authMutex`, so concurrent 401s collapse
 * into a single refresh.
 */
export async function recoverFromAuthFailure<T>(retry: () => Promise<T>): Promise<T | null> {
  if (!authMutex.isLocked()) {
    const release = await authMutex.acquire();
    try {
      const refreshed = await refreshAuthToken();
      if (refreshed) {
        return await retry();
      }
      // Refresh token is dead. We don't sign out here — Amplify's
      // refreshAuthTokens path dispatches `tokenRefresh_failure` on the Hub
      // when the refresh fails, and the Hub listener in AuthenticatedApp.tsx
      // handles clearCognitoStorage() + signOut(). Doing it twice triggers
      // two RevokeToken Cognito calls (the second one fails with errors in
      // the console because the local tokens are already gone).
      console.error('recoverFromAuthFailure: refresh failed; Hub listener will sign out');
      return null;
    } finally {
      release();
    }
  }

  await authMutex.waitForUnlock();
  return await retry();
}

/**
 * Drop-in fetch() replacement for code paths that can't go through RTK Query
 * (file streams, blob downloads, ad-hoc admin endpoints). Same auth + refresh +
 * signOut behavior as the RTK Query interceptor.
 *
 * If the refresh ultimately fails the original 401 Response is returned to the
 * caller so its existing error-handling path still fires; signOut runs in parallel
 * and the Authenticator unmounts the page.
 */
export async function authenticatedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const buildRequest = async (): Promise<RequestInit> => {
    const token = await getAuthIdToken();
    const headers = new Headers(init.headers ?? {});
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return { ...init, headers };
  };

  await authMutex.waitForUnlock();
  let response = await fetch(input, await buildRequest());

  if (response.status === 401) {
    const retried = await recoverFromAuthFailure(async () => fetch(input, await buildRequest()));
    if (retried) {
      response = retried;
    }
  }

  return response;
}
